import { useEffect, useMemo, useState } from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Button,
  Switch,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Loading, useToast } from '@vnetwork/ui/feedback';
import { useEventJobConfig } from '@/api/media/live-event-jobs';
import {
  useCreateLmsSettingsTemplate,
  useLmsSettingsTemplateDetail,
  useUpdateLmsSettingsTemplate,
} from '@/api/media/live-settings-templates';
import type {
  CreateLmsSettingsTemplateRequest,
  LmsSettingsTemplateInput,
  LmsSettingsTemplateLiveType,
  LmsSettingsTemplateOutput,
  LmsSettingsTemplateRecordingType,
} from '@/api/media/live-settings-templates-types';
import styles from './SettingsTemplates.module.css';

const INPUT_OPTIONS: { value: LmsSettingsTemplateInput; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
];

const OUTPUT_OPTIONS: { value: LmsSettingsTemplateOutput; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
  { value: 'http-flv', label: 'HTTP-FLV' },
  { value: 'hls', label: 'HLS' },
  { value: 'dash', label: 'MPEG-DASH' },
];

const RECORDING_OPTIONS: { value: LmsSettingsTemplateRecordingType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'hls', label: 'HLS' },
  { value: 'mp4', label: 'MP4' },
];

const LIVE_TYPE_OPTIONS: { value: LmsSettingsTemplateLiveType; label: string }[] = [
  { value: 'linear', label: 'Live' },
  { value: 'event', label: 'Event' },
];

const DEFAULT_FORM: CreateLmsSettingsTemplateRequest = {
  inbound_domain: '',
  app: '',
  stream: '',
  input: 'rtmp',
  output: '',
  preset: '',
  recording_type: 'none',
  storage: '',
  live_type: 'linear',
  low_latency: false,
};

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

export interface SettingsTemplateCreateDialogProps {
  open: boolean;
  serviceId: string;
  /** When set, dialog operates in edit mode loading the existing template */
  templateId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

/** Create or edit a Domain Template (settings template). Payload parity: output/preset sent as comma-joined strings. */
export function SettingsTemplateCreateDialog({
  open,
  serviceId,
  templateId,
  onClose,
  onSuccess,
}: SettingsTemplateCreateDialogProps) {
  const isEdit = !!templateId;
  const { show } = useToast();

  const [form, setForm] = useState<CreateLmsSettingsTemplateRequest>(DEFAULT_FORM);
  const [outputs, setOutputs] = useState<LmsSettingsTemplateOutput[]>([]);
  const [presets, setPresets] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: config, isLoading: isConfigLoading } = useEventJobConfig(serviceId);
  const { data: detail, isLoading: isDetailLoading } = useLmsSettingsTemplateDetail(
    serviceId,
    templateId ?? 0,
    { enabled: isEdit && !!templateId },
  );

  const createMut = useCreateLmsSettingsTemplate(serviceId);
  const updateMut = useUpdateLmsSettingsTemplate(serviceId);
  const isSubmitting = createMut.isPending || updateMut.isPending;

  const presetOptions = useMemo(() => {
    const base = (config?.presets ?? [])
      .filter((p) => p.value !== '__snapshot__')
      .map((p) => ({ value: p.value, label: p.value === 'transmux' ? 'Transmuxing only' : p.label }));
    if (!base.some((p) => p.value === 'audio_only')) base.push({ value: 'audio_only', label: 'Audio Only' });
    return base;
  }, [config?.presets]);

  // Populate form from detail when editing
  useEffect(() => {
    if (!detail) return;
    setForm({
      inbound_domain: detail.inbound_domain || '',
      app: detail.app || '',
      stream: detail.stream || '',
      input: 'rtmp',
      output: detail.output || '',
      preset: detail.preset || '',
      recording_type: (detail.recording_type || 'none') as LmsSettingsTemplateRecordingType,
      storage: detail.storage || '',
      live_type: detail.live_type,
      low_latency: !!detail.low_latency,
    });
    setOutputs((detail.output || '').split(',').filter(Boolean) as LmsSettingsTemplateOutput[]);
    setPresets((detail.preset || '').split(',').filter(Boolean));
    setErrors({});
  }, [detail]);

  // Reset on open (create mode)
  useEffect(() => {
    if (open && !isEdit) {
      setForm(DEFAULT_FORM);
      setOutputs([]);
      setPresets([]);
      setErrors({});
    }
  }, [open, isEdit]);

  const setField = <K extends keyof CreateLmsSettingsTemplateRequest>(
    field: K,
    value: CreateLmsSettingsTemplateRequest[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.inbound_domain.trim()) next.inbound_domain = 'Inbound domain is required.';
    if (!form.app.trim()) next.app = 'App is required.';
    if (!form.stream.trim()) next.stream = 'Stream is required.';
    if (outputs.length === 0) next.output = 'Select at least one output.';
    if (presets.length === 0) next.preset = 'Select at least one preset.';
    if (form.recording_type !== 'none' && !form.storage.trim())
      next.storage = 'Storage is required when recording is enabled.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload: CreateLmsSettingsTemplateRequest = {
      ...form,
      inbound_domain: form.inbound_domain.trim(),
      app: form.app.trim(),
      stream: form.stream.trim(),
      output: outputs.join(','),
      preset: presets.join(','),
      storage: form.storage.trim(),
    };
    const onSuccessHandler = () => {
      show({ tone: 'success', title: isEdit ? 'Template updated' : 'Template created' });
      onSuccess();
      onClose();
    };
    const onErrorHandler = (e: Error) =>
      show({ tone: 'danger', title: e.message || (isEdit ? 'Failed to update template' : 'Failed to create template') });

    if (isEdit && templateId) {
      updateMut.mutate({ id: templateId, payload }, { onSuccess: onSuccessHandler, onError: onErrorHandler });
    } else {
      createMut.mutate(payload, { onSuccess: onSuccessHandler, onError: onErrorHandler });
    }
  };

  const title = isEdit ? 'Update Domain Template' : 'Create Domain Template';
  const isLoading = isDetailLoading || isConfigLoading;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {isDetailLoading ? (
          <Loading label="Loading template…" />
        ) : (
          <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Inbound Domain" required error={errors.inbound_domain}>
              {(fp) => (
                <Input
                  {...fp}
                  value={form.inbound_domain}
                  onChange={(e) => setField('inbound_domain', e.target.value)}
                  placeholder="live.example.com"
                />
              )}
            </Field>

            <div className={styles.grid2}>
              <Field label="App" required error={errors.app}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.app}
                    onChange={(e) => setField('app', e.target.value)}
                    placeholder="test_app"
                  />
                )}
              </Field>
              <Field label="Stream" required error={errors.stream}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.stream}
                    onChange={(e) => setField('stream', e.target.value)}
                    placeholder="test_stream"
                  />
                )}
              </Field>
            </div>

            <div className={styles.grid2}>
              <Field label="Input" required>
                {(fp) => (
                  <Select value={form.input} onValueChange={(v) => setField('input', v as LmsSettingsTemplateInput)}>
                    <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INPUT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Live Type" required>
                {(fp) => (
                  <Select value={form.live_type} onValueChange={(v) => setField('live_type', v as LmsSettingsTemplateLiveType)}>
                    <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LIVE_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <fieldset className={styles.fieldset}>
              <legend className={styles.fieldLegend}>
                Outputs <span aria-hidden="true">*</span>
              </legend>
              <div className={styles.checkGroup}>
                {OUTPUT_OPTIONS.map(({ value, label }) => (
                  <label key={value} className={styles.checkItem}>
                    <Checkbox
                      checked={outputs.includes(value)}
                      onCheckedChange={() => {
                        setOutputs((prev) => toggle(prev, value));
                        setErrors((prev) => { const n = { ...prev }; delete n.output; return n; });
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {errors.output && (
                <p className={styles.fieldError}>
                  {errors.output}
                </p>
              )}
            </fieldset>

            <fieldset className={styles.fieldset}>
              <legend className={styles.fieldLegend}>
                Presets <span aria-hidden="true">*</span>
              </legend>
              <div className={styles.checkGroup}>
                {isConfigLoading ? (
                  <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                    Loading presets…
                  </span>
                ) : (
                  presetOptions.map(({ value, label }) => (
                    <label key={value} className={styles.checkItem}>
                      <Checkbox
                        checked={presets.includes(value)}
                        onCheckedChange={() => {
                          setPresets((prev) => toggle(prev, value));
                          setErrors((prev) => { const n = { ...prev }; delete n.preset; return n; });
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  ))
                )}
              </div>
              {errors.preset && (
                <p className={styles.fieldError}>
                  {errors.preset}
                </p>
              )}
            </fieldset>

            <div className={styles.grid2}>
              <Field label="Recording Type" required>
                {(fp) => (
                  <Select
                    value={form.recording_type}
                    onValueChange={(v) => setField('recording_type', v as LmsSettingsTemplateRecordingType)}
                  >
                    <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RECORDING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <div className={styles.switchAlignEnd}>
                <label className={styles.checkItem}>
                  <Switch
                    checked={form.low_latency}
                    onCheckedChange={(v) => setField('low_latency', v)}
                    id="tmpl-low-latency"
                  />
                  <span>Enable low latency</span>
                </label>
              </div>
            </div>

            <Field
              label="Storage"
              required={form.recording_type !== 'none'}
              error={errors.storage}
              description="Required only when recording type is HLS or MP4."
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={form.storage}
                  onChange={(e) => setField('storage', e.target.value)}
                  placeholder="ftp://user:pass@127.0.0.1:21/test"
                />
              )}
            </Field>
          </DialogBody>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || isLoading}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
